/**
 * CDM Network — F5 virtual servers, pools, policies + (future) Cisco
 * interfaces, Palo Alto policies, firewall rules.
 *
 * Per docs/cdm-design-v4.md §7: declared connectivity layer. The
 * observed layer (NetFlow / VPC flow / Zeek) joins to this in Phase 12
 * for shadow-path detection and blast-radius walking.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Network, RefreshCw, ChevronLeft, Search, Filter,
  Globe, Server, Shield,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface NetworkObject {
  golden_id: string
  name: string | null
  object_kind: string
  network_device: string | null
  dest_ip: string | null
  dest_port: number | null
  exposed_ports: number[] | null
  sources: string[]
  last_observed: string | null
  last_change: string | null
}

const KIND_TONE: Record<string, string> = {
  virtual_server:   'bg-emerald/10 text-emerald border-emerald/30',
  pool:             'bg-blue/10 text-blue-light border-blue/30',
  policy:           'bg-purple/10 text-purple-light border-purple/30',
  firewall_policy:  'bg-rose/10 text-rose-light border-rose/30',
}

export default function CdmNetworkPage() {
  const [objects, setObjects] = useState<NetworkObject[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [kindFilter, setKindFilter] = useState('')

  const load = () => {
    setLoading(true)
    api.get('/api/cdm/network', { params: { limit: 500, kind: kindFilter || undefined } })
      .then(r => setObjects(r.data?.objects || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [kindFilter])

  const kinds = useMemo(() => {
    const s = new Set(objects.map(o => o.object_kind))
    return Array.from(s).sort()
  }, [objects])

  const filtered = useMemo(() =>
    objects.filter(o => !search ||
      o.name?.toLowerCase().includes(search.toLowerCase()) ||
      o.network_device?.toLowerCase().includes(search.toLowerCase()) ||
      o.dest_ip?.includes(search)),
  [objects, search])

  const counts = useMemo(() => {
    const c: Record<string, number> = {}
    for (const o of objects) c[o.object_kind] = (c[o.object_kind] || 0) + 1
    return c
  }, [objects])

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <Link to="/app/cdm" className="text-text-muted hover:text-text-primary inline-flex items-center gap-1">
          <ChevronLeft size={14} /> CDM
        </Link>
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Network size={18} className="text-emerald" /> CDM NETWORK
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          F5 · Cisco · Palo Alto · firewall policies · declared connectivity
        </span>
        <button type="button" onClick={load} disabled={loading} className="btn-ghost ml-auto">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Virtual servers" value={counts.virtual_server || 0} tone="text-emerald" />
        <Tile label="Pools"             value={counts.pool || 0}           tone="text-blue-light" />
        <Tile label="LTM policies"      value={counts.policy || 0}         tone="text-purple-light" />
        <Tile label="Firewall policies" value={counts.firewall_policy || 0} tone="text-rose-light" />
      </div>

      <div className="flex items-center gap-2 text-xs flex-wrap">
        <Filter size={12} className="text-text-muted" />
        <select className="input" value={kindFilter} onChange={e => setKindFilter(e.target.value)}
                aria-label="Filter by kind">
          <option value="">All kinds</option>
          {kinds.map(k => <option key={k} value={k}>{k}</option>)}
        </select>
        <div className="relative flex-1 max-w-sm">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-muted" />
          <input type="search" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="search name / device / dest IP" className="input pl-7 w-full" />
        </div>
        <span className="text-text-muted ml-auto">{filtered.length} of {objects.length}</span>
      </div>

      <div className="card p-0 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-surface-raised">
            <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Kind</th>
              <th className="text-left px-3 py-2">Device</th>
              <th className="text-left px-3 py-2">Dest</th>
              <th className="text-left px-3 py-2">Sources</th>
              <th className="text-left px-3 py-2">Last seen</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-text-muted">
                No network objects yet. Configure the F5 connector under
                /app/connectors-manage and trigger a CDM pull (the F5 CDM adapter
                runs every 2h at :55 by default).
              </td></tr>
            )}
            {filtered.map(o => (
              <tr key={o.golden_id} className="hover:bg-surface-raised/40">
                <td className="px-3 py-1.5">
                  <Link to={`/app/cdm/investigation/${o.golden_id}`}
                        className="text-blue-light hover:underline font-mono text-[11px]">
                    {o.name || '—'}
                  </Link>
                </td>
                <td className="px-3 py-1.5">
                  <span className={clsx('badge text-[9px] border',
                                       KIND_TONE[o.object_kind] || 'bg-surface-raised text-text-muted border-border')}>
                    {o.object_kind}
                  </span>
                </td>
                <td className="px-3 py-1.5 text-purple-light">{o.network_device || '—'}</td>
                <td className="px-3 py-1.5 font-mono text-[11px] text-text-secondary">
                  {o.dest_ip ? `${o.dest_ip}${o.dest_port ? ':' + o.dest_port : ''}` : '—'}
                </td>
                <td className="px-3 py-1.5">
                  {o.sources.map(s => (
                    <span key={s} className="badge text-[9px] mr-1 bg-surface-raised text-text-muted border border-border">{s}</span>
                  ))}
                </td>
                <td className="px-3 py-1.5 font-mono text-[10px] text-text-muted">
                  {o.last_observed ? new Date(o.last_observed).toLocaleString() : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[10px] text-text-muted text-center">
        Phase 12 adds the observed layer (NetFlow / VPC flow / Zeek) and joins
        to compute confirmed paths, unused permissions, and shadow paths.
      </p>
    </div>
  )
}


function Tile({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div className="card p-3">
      <div className="text-[10px] uppercase tracking-widest text-text-muted">{label}</div>
      <div className={clsx('text-2xl font-black font-mono mt-1', tone)}>{value.toLocaleString()}</div>
    </div>
  )
}
